import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Web3Context } from '../../context/Web3Context';
import './Landing.css';

const Landing = () => {
  const { account, isRegistered, connectWallet, isConnecting } = useContext(Web3Context);
  const navigate = useNavigate();

  useEffect(() => {
    if (account && isRegistered) {
      navigate('/dashboard');
    }
  }, [account, isRegistered, navigate]);

  return (
    <div className="landing">
      <div className="landing-bg">
        <div className="frost-overlay"></div>
        <div className="crystal-bg-1"></div>
        <div className="crystal-bg-2"></div>
        <div className="crystal-bg-3"></div>
        {Array.from({ length: 30 }).map((_, i) => (
          <div key={i} className="bg-particle" style={{
            '--x': `${Math.random() * 100}%`,
            '--y': `${Math.random() * 100}%`,
            '--delay': `${Math.random() * 5}s`,
            '--size': `${Math.random() * 3 + 1}px`,
            '--duration': `${Math.random() * 4 + 3}s`,
          }}></div>
        ))}
      </div>

      <div className="landing-content">
        <div className="landing-hero">
          <img src="/logo.png" alt="UltraCapital" className="hero-logo" />
          <div className="hero-badge">Premium DeFi Ecosystem</div>
          <h1 className="hero-title">
            <span className="title-line">Ultra</span>
            <span className="title-line accent">Capital</span>
          </h1>
          <p className="hero-subtitle">
            The next generation decentralized investment platform powered by blockchain.
            Multi-tier matrix system, reward pools, and transparent income distribution.
          </p>

          <div className="hero-cta">
            {!account ? (
              <button className="cta-btn cta-primary" onClick={connectWallet} disabled={isConnecting}>
                <span className="cta-glow"></span>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="6" width="20" height="14" rx="2" strokeLinecap="round"/><path d="M2 10h20" strokeLinecap="round"/><circle cx="17" cy="15" r="1.5" fill="currentColor"/>
                </svg>
                <span>{isConnecting ? 'Connecting...' : 'Connect Wallet'}</span>
              </button>
            ) : !isRegistered ? (
              <button className="cta-btn cta-primary" onClick={() => navigate('/register')}>
                <span className="cta-glow"></span>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Join Now - $10 USDT</span>
              </button>
            ) : null}
          </div>

          <div className="hero-highlights">
            <div className="highlight-item">
              <div className="highlight-number">6</div>
              <div className="highlight-label">Investment<br/>Packages</div>
            </div>
            <div className="highlight-divider"></div>
            <div className="highlight-item">
              <div className="highlight-number">12</div>
              <div className="highlight-label">Matrix<br/>Levels</div>
            </div>
            <div className="highlight-divider"></div>
            <div className="highlight-item">
              <div className="highlight-number">6</div>
              <div className="highlight-label">Reward<br/>Pools</div>
            </div>
            <div className="highlight-divider"></div>
            <div className="highlight-item">
              <div className="highlight-number">3x</div>
              <div className="highlight-label">Ceiling<br/>Returns</div>
            </div>
          </div>

          <div className="hero-features">
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Smart Matrix</h3>
              <p>2-wide, 12-level deep auto-placement matrix with spillover system</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Instant Income</h3>
              <p>50% sponsor income paid directly to wallet. Level income claimable anytime</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Reward Pools</h3>
              <p>Gold, Platinum, Diamond, Crown, Blue & Ambassador pool distributions</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Secure & Transparent</h3>
              <p>Fully on-chain smart contract. Verified, audited, no hidden fees</p>
            </div>
          </div>

          <div className="income-overview">
            <h2 className="section-heading">Income Structure</h2>
            <div className="income-grid">
              <div className="income-item">
                <span className="income-pct">50%</span>
                <span className="income-name">Sponsor Income</span>
                <span className="income-desc">Direct to wallet</span>
              </div>
              <div className="income-item">
                <span className="income-pct">30%</span>
                <span className="income-name">Level Income</span>
                <span className="income-desc">2.5% x 12 levels</span>
              </div>
              <div className="income-item">
                <span className="income-pct">20%</span>
                <span className="income-name">Pool Distribution</span>
                <span className="income-desc">6 reward pools</span>
              </div>
            </div>
          </div>

          <div className="packages-overview">
            <h2 className="section-heading">Packages</h2>
            <div className="pkg-overview-grid">
              {['Starter $10', 'Bronze $20', 'Silver $30', 'Gold $70', 'Platinum $120', 'Diamond $250'].map((pkg, i) => (
                <div key={i} className="pkg-overview-item">
                  <span className="pkg-ov-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="pkg-ov-name">{pkg}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
